import { useState, useCallback, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  YARDI_INVITE_LIST_SESSION_KEY,
  YARDI_INVITE_LIST_DEFAULTS,
} from '../constants'
import { buildYardiInviteListParams } from '../utils/yardiInviteListParams'
import {
  fetchYardiInviteList,
  fetchAllCompanyList,
  fetchAllPropertyList,
  postYardiApplicationRetry,
  getApplicationStatus,
  applyInviteStatusArrayKey,
} from '../services/yardiInviteService'
import type { ApplicationStatusResult, ListFilterForm, YardiInviteRow } from '../types'
import {
  readSessionJson,
  writeSessionJson,
  clearSessionKey,
  extractYardiInviteListFormFromSession,
} from './sessionFilters'
import { showError, showSuccess, formatApiError } from '@/lib/utils/notifications'

const emptyForm = (): ListFilterForm => ({
  q: '',
  org_id: '',
  company_id: '',
  property_id: '',
})

export function useYardiInviteList(opts: {
  orgId: string | null
  companyId: string | null
  propertyId: string | null
  orgName: string | null
  companyName: string | null
  propertyName: string | null
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [invites, setInvites] = useState<YardiInviteRow[] | null>(null)
  const [processing, setProcessing] = useState(false)
  const [retryingApplicationId, setRetryingApplicationId] = useState<number | null>(null)
  /** Retry error: show confirm modal instead of toast. */
  const [retryErrorModal, setRetryErrorModal] = useState<{ title: string; bodyHtml: string } | null>(null)
  const [filterForm, setFilterForm] = useState<ListFilterForm>(emptyForm)
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([])
  const [propertyOptions, setPropertyOptions] = useState<{ value: string; label: string }[]>([])
  const [readOnlyControl, setReadOnlyControl] = useState<string[]>([])
  const [pageOptions, setPageOptions] = useState({ current: 1, last: 1, total: 0 })
  const [sortBy, setSortBy] = useState<'asc' | 'desc'>(YARDI_INVITE_LIST_DEFAULTS.sortBy)
  const [orderBy, setOrderBy] = useState(YARDI_INVITE_LIST_DEFAULTS.orderBy)
  const [perPage, setPerPage] = useState(YARDI_INVITE_LIST_DEFAULTS.perPage)
  /** Captured once on mount so invite_code merges after session/role locks. */
  const [initialInviteCode] = useState(() => searchParams.get('invite_code'))

  const allPropertyRef = useRef<Record<string, string>>({})
  const allCompanyRef = useRef<Record<string, string>>({})
  const allOrgRef = useRef<Record<string, string>>({})
  const onPageLoadRef = useRef(true)
  /** List GET only — separate from company/property dropdown requests. */
  const listFetchRequestIdRef = useRef(0)
  const companyRequestIdRef = useRef(0)
  const propertyRequestIdRef = useRef(0)
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const filterFormRef = useRef(filterForm)
  filterFormRef.current = filterForm
  const sortByRef = useRef(sortBy)
  sortByRef.current = sortBy
  const orderByRef = useRef(orderBy)
  orderByRef.current = orderBy
  const perPageRef = useRef(perPage)
  perPageRef.current = perPage
  const pageOptionsRef = useRef(pageOptions)
  pageOptionsRef.current = pageOptions

  const applyRoleLocks = useCallback(
    (form: ListFilterForm, sessionHadOrgId: boolean): ListFilterForm => {
      const ro: string[] = []
      let next = { ...form }
      if (opts.orgId && !sessionHadOrgId) {
        ro.push('organization')
        allOrgRef.current[opts.orgId] = opts.orgName || opts.orgId
        next = { ...next, org_id: opts.orgId }
      }
      if (opts.companyId) {
        ro.push('company')
        allCompanyRef.current[opts.companyId] = opts.companyName || opts.companyId
        next = { ...next, company_id: opts.companyId }
        setCompanyOptions([{ value: opts.companyId, label: opts.companyName || opts.companyId }])
      }
      if (opts.propertyId) {
        ro.push('property')
        allPropertyRef.current[opts.propertyId] = opts.propertyName || opts.propertyId
        next = { ...next, property_id: opts.propertyId }
        setPropertyOptions([{ value: opts.propertyId, label: opts.propertyName || opts.propertyId }])
      }
      setReadOnlyControl(ro)
      return next
    },
    [opts.orgId, opts.companyId, opts.propertyId, opts.orgName, opts.companyName, opts.propertyName],
  )

  const buildListParams = useCallback((page: number, form: ListFilterForm, sb: 'asc' | 'desc', ob: string, pp: number) => {
    const sessionFilter = readSessionJson<Record<string, unknown>>(YARDI_INVITE_LIST_SESSION_KEY)
    // Merge session only on first build; clear the flag afterward so later requests
    // (pagination/sort) don't re-merge a stale saved page.
    const wasFirstBuild = onPageLoadRef.current
    const shouldMergeSession = !!(wasFirstBuild && sessionFilter)
    if (wasFirstBuild) {
      onPageLoadRef.current = false
    }
    return buildYardiInviteListParams(page, form, sb, ob, pp, {
      apply: shouldMergeSession,
      snapshot: sessionFilter,
    })
  }, [])

  const saveFilter = useCallback(
    (urlParams: Record<string, string | number | boolean | string[] | undefined>) => {
      const filterData: Record<string, unknown> = { ...urlParams }
      const pid = urlParams.property_id
      if (pid && allPropertyRef.current[String(pid)]) {
        filterData.property_name = allPropertyRef.current[String(pid)]
      }
      const cid = urlParams.company_id
      if (cid && allCompanyRef.current[String(cid)]) {
        filterData.company_name = allCompanyRef.current[String(cid)]
      }
      const oid = urlParams.org_id
      if (oid && allOrgRef.current[String(oid)]) {
        filterData.org_name = allOrgRef.current[String(oid)]
      }
      writeSessionJson(YARDI_INVITE_LIST_SESSION_KEY, filterData)
    },
    [],
  )

  const loadInvites = useCallback(
    async (urlParams: Record<string, string | number | boolean | string[] | undefined>) => {
      const id = ++listFetchRequestIdRef.current
      setProcessing(true)
      try {
        const withStatus = applyInviteStatusArrayKey(urlParams)
        const res = await fetchYardiInviteList(withStatus)
        if (id !== listFetchRequestIdRef.current) return
        setInvites(res.data || [])
        setPageOptions({
          current: res.current || 1,
          last: res.last || 1,
          total: res.total || 0,
        })
      } catch (e) {
        showError(formatApiError(e), false)
      } finally {
        if (id === listFetchRequestIdRef.current) setProcessing(false)
      }
    },
    [],
  )

  const runTriggerFilter = useCallback(
    (form: ListFilterForm, page = 1) => {
      const sb = sortByRef.current
      const ob = orderByRef.current
      const pp = perPageRef.current
      const nextParams = buildListParams(page, form, sb, ob, pp)
      saveFilter(nextParams)
      window.setTimeout(() => loadInvites(nextParams), 2)
    },
    [buildListParams, saveFilter, loadInvites],
  )

  useEffect(() => {
    const session = readSessionJson<Record<string, unknown>>(YARDI_INVITE_LIST_SESSION_KEY)
    let form = emptyForm()
    if (session) {
      if (session.org_id && session.org_name) {
        allOrgRef.current[String(session.org_id)] = String(session.org_name)
      }
      if (session.property_id && session.property_name) {
        allPropertyRef.current[String(session.property_id)] = String(session.property_name)
        setPropertyOptions([
          { value: String(session.property_id), label: String(session.property_name) },
        ])
      }
      if (session.company_id && session.company_name) {
        allCompanyRef.current[String(session.company_id)] = String(session.company_name)
        setCompanyOptions([
          { value: String(session.company_id), label: String(session.company_name) },
        ])
      }
      Object.assign(form, extractYardiInviteListFormFromSession(session as Record<string, unknown>))
      if (session.perPage) setPerPage(Number(session.perPage) || YARDI_INVITE_LIST_DEFAULTS.perPage)
      if (session.sortBy === 'asc' || session.sortBy === 'desc') setSortBy(session.sortBy)
      if (typeof session.orderBy === 'string') setOrderBy(session.orderBy)
    }
    const sessionHadOrg = !!(session && session.org_id)
    form = applyRoleLocks(form, sessionHadOrg)

    if (initialInviteCode) {
      form = { ...form, q: initialInviteCode }
      window.history.replaceState(window.history.state, '', 'invite')
      setSearchParams({}, { replace: true })
    }

    filterFormRef.current = form
    setFilterForm(form)
    perPageRef.current =
      (session?.perPage != null ? Number(session.perPage) : null) || YARDI_INVITE_LIST_DEFAULTS.perPage
    if (session?.sortBy === 'asc' || session?.sortBy === 'desc') sortByRef.current = session.sortBy
    if (typeof session?.orderBy === 'string') orderByRef.current = session.orderBy

    runTriggerFilter(form, 1)
    // setSearchParams intentionally excluded from deps — its changing identity would loop this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount + initialInviteCode only
  }, [initialInviteCode])

  const filterSubmit = useCallback(() => {
    runTriggerFilter(filterFormRef.current, 1)
  }, [runTriggerFilter])

  const resetFilter = useCallback(() => {
    clearSessionKey(YARDI_INVITE_LIST_SESSION_KEY)
    onPageLoadRef.current = true
    setSortBy(YARDI_INVITE_LIST_DEFAULTS.sortBy)
    setOrderBy(YARDI_INVITE_LIST_DEFAULTS.orderBy)
    setPerPage(YARDI_INVITE_LIST_DEFAULTS.perPage)
    sortByRef.current = YARDI_INVITE_LIST_DEFAULTS.sortBy
    orderByRef.current = YARDI_INVITE_LIST_DEFAULTS.orderBy
    perPageRef.current = YARDI_INVITE_LIST_DEFAULTS.perPage
    setCompanyOptions([])
    setPropertyOptions([])
    allPropertyRef.current = {}
    allCompanyRef.current = {}
    allOrgRef.current = {}
    const fresh = applyRoleLocks(emptyForm(), false)
    setFilterForm(fresh)
    runTriggerFilter(fresh, 1)
  }, [applyRoleLocks, runTriggerFilter])

  const sortColumn = useCallback(
    (col: string) => {
      const nextSort = sortByRef.current === 'asc' ? 'desc' : 'asc'
      setSortBy(nextSort)
      setOrderBy(col)
      sortByRef.current = nextSort
      orderByRef.current = col
      const nextParams = buildListParams(1, filterFormRef.current, nextSort, col, perPageRef.current)
      saveFilter(nextParams)
      window.setTimeout(() => loadInvites(nextParams), 2)
    },
    [buildListParams, saveFilter, loadInvites],
  )

  const paginateItem = useCallback(
    (pageNo: number) => {
      let p = Number(pageNo)
      if (!Number.isFinite(p) || p < 1) return
      const last = pageOptionsRef.current.last || 1
      if (last >= 1 && p > last) p = last
      const nextParams = buildListParams(
        p,
        filterFormRef.current,
        sortByRef.current,
        orderByRef.current,
        perPageRef.current,
      )
      saveFilter(nextParams)
      loadInvites(nextParams)
    },
    [buildListParams, saveFilter, loadInvites],
  )

  const changePerPage = useCallback(
    (pp: number) => {
      if (!pp) return
      setPerPage(pp)
      perPageRef.current = pp
      const nextParams = buildListParams(1, filterFormRef.current, sortByRef.current, orderByRef.current, pp)
      saveFilter(nextParams)
      window.setTimeout(() => loadInvites(nextParams), 2)
    },
    [buildListParams, saveFilter, loadInvites],
  )

  const loadCompany = useCallback(
    async (searchText?: string) => {
      const id = ++companyRequestIdRef.current
      /** Do not use `processing` — it is reserved for the list fetch. */
      try {
        const rows = await fetchAllCompanyList({
          q: searchText,
          org_id: filterFormRef.current.org_id || undefined,
        })
        if (id !== companyRequestIdRef.current) return
        const opts = rows.map((c) => ({
          value: String(c.company_id),
          label: c.company_name || String(c.company_id),
        }))
        setCompanyOptions(opts)
        rows.forEach((c) => {
          allCompanyRef.current[String(c.company_id)] = c.company_name || String(c.company_id)
        })
        if (opts.length === 1) {
          window.setTimeout(() => {
            if (id !== companyRequestIdRef.current) return
            const v = opts[0].value
            filterFormRef.current = { ...filterFormRef.current, company_id: v }
            setFilterForm((f) => ({ ...f, company_id: v }))
          }, 10)
        }
      } catch (e) {
        showError(formatApiError(e), false)
      }
    },
    [],
  )

  const loadProperty = useCallback(async (searchText?: string) => {
    const id = ++propertyRequestIdRef.current
    try {
      const rows = await fetchAllPropertyList({
        q: searchText,
        company_id: filterFormRef.current.company_id || undefined,
        org_id: filterFormRef.current.org_id || undefined,
      })
      if (id !== propertyRequestIdRef.current) return
      const opts = rows.map((p) => ({
        value: String(p.property_id),
        label: p.property_name || String(p.property_id),
      }))
      setPropertyOptions(opts)
      rows.forEach((p) => {
        allPropertyRef.current[String(p.property_id)] = p.property_name || String(p.property_id)
      })
      // After refresh: if exactly one property, select it; on a non-search load keep the
      // current property_id, prepending a synthetic option if it's missing from the list.
      const searchEmpty = !searchText?.trim()
      window.setTimeout(() => {
        if (id !== propertyRequestIdRef.current) return
        const pid = filterFormRef.current.property_id
        if (opts.length === 1) {
          const v = opts[0].value
          filterFormRef.current = { ...filterFormRef.current, property_id: v }
          setFilterForm((f) => ({ ...f, property_id: v }))
          return
        }
        if (searchEmpty && pid) {
          const has = opts.some((o) => o.value === pid)
          if (!has) {
            const label = allPropertyRef.current[pid] || pid
            setPropertyOptions([{ value: pid, label }, ...opts])
          }
          filterFormRef.current = { ...filterFormRef.current, property_id: pid }
          setFilterForm((f) => ({ ...f, property_id: pid }))
        }
      }, 10)
    } catch (e) {
      showError(formatApiError(e), false)
    }
  }, [])

  /** Debounced in the UI; runs the query for the latest string. */
  const searchCompany = useCallback(
    (text: string) => {
      void loadCompany(text)
    },
    [loadCompany],
  )

  const searchProperty = useCallback(
    (text: string) => {
      void loadProperty(text)
    },
    [loadProperty],
  )

  /** Reload the property dropdown on company change unless the role locks property. */
  const companySelected = useCallback(
    (_companyId: string) => {
      if (opts.propertyId) return
      setPropertyOptions([])
      void loadProperty('')
    },
    [opts.propertyId, loadProperty],
  )

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
  }, [])

  const clearRetryErrorModal = useCallback(() => setRetryErrorModal(null), [])

  /** Merge status result into the visible row — avoids refetching the whole list on retry. */
  const mergeInviteFromStatusResult = useCallback(
    (applicationId: number, result: ApplicationStatusResult | null | undefined) => {
      if (!result || typeof result !== 'object') return
      setInvites((prev) => {
        if (!prev?.length) return prev
        const i = prev.findIndex((r) => r.yardi_application_id === applicationId)
        if (i === -1) return prev
        const next = [...prev]
        const cur = next[i]
        const terminalOk =
          result.state === 'INVITE_SENT' || result.state === 'PROCESSED'
        next[i] = {
          ...cur,
          ...(result.state !== undefined ? { state: result.state } : {}),
          ...(result.lr_check_error !== undefined
            ? {
                lr_check_error:
                  result.lr_check_error === null
                    ? undefined
                    : (result.lr_check_error as YardiInviteRow['lr_check_error']),
              }
            : terminalOk
              ? { lr_check_error: undefined }
              : {}),
        }
        return next
      })
    },
    [],
  )

  const applicationStatusPolling = useCallback(
    (applicationId: number, interval: number) => {
      getApplicationStatus(applicationId)
        .then((result) => {
          mergeInviteFromStatusResult(applicationId, result)
          const state =
            result && typeof result === 'object' && 'state' in result
              ? (result as ApplicationStatusResult).state
              : undefined
          if (state === 'INQUEUE') {
            pollingTimeoutRef.current = setTimeout(
              () => applicationStatusPolling(applicationId, interval),
              interval,
            )
          }
        })
        .catch(() => {
          /* no-op */
        })
      // Don't touch `processing` here — it's reserved for the list fetch spinner.
    },
    [mergeInviteFromStatusResult],
  )

  const retryapplication = useCallback(
    async (applicationId: number) => {
      setRetryErrorModal(null)
      setRetryingApplicationId(applicationId)
      try {
        await postYardiApplicationRetry(applicationId)
        showSuccess('Yardi application retry has been successfully triggered!')
        mergeInviteFromStatusResult(applicationId, {
          state: 'INQUEUE',
          lr_check_error: null,
        })
        applicationStatusPolling(applicationId, 3000)
      } catch (e) {
        setRetryErrorModal({ title: 'Error', bodyHtml: formatApiError(e) })
      } finally {
        // Never set `processing` during retry — it would swap the table for a spinner.
        setRetryingApplicationId(null)
      }
    },
    [mergeInviteFromStatusResult, applicationStatusPolling],
  )

  useEffect(() => () => stopPolling(), [stopPolling])

  /** Update form state and `filterFormRef` synchronously so the same event tick sees the new form. */
  const patchFilterForm = useCallback((partial: Partial<ListFilterForm>) => {
    const n = { ...filterFormRef.current, ...partial }
    filterFormRef.current = n
    setFilterForm(n)
  }, [])

  return {
    invites,
    processing,
    retryingApplicationId,
    isRetryCallOngoing: retryingApplicationId != null,
    filterForm,
    patchFilterForm,
    companyOptions,
    propertyOptions,
    readOnlyControl,
    pageOptions,
    sortBy,
    orderBy,
    perPage,
    filterSubmit,
    resetFilter,
    sortColumn,
    paginateItem,
    changePerPage,
    searchCompany,
    searchProperty,
    companySelected,
    loadCompany,
    loadProperty,
    retryapplication,
    retryErrorModal,
    clearRetryErrorModal,
  }
}
